import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import './DayButton.scss';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * DayButton component.
 * This component is used to display a day button with a date and a price.
 * @param {object} props - onClick, leftIcon, date, rightIcon, price, disabled, hasRadius
 * @param {function} props.onClick - Function to be called when the button is clicked
 * @param {boolean} props.leftIcon - Whether to show the left icon
 * @param {string} props.date - Date to be displayed
 * @param {boolean} props.rightIcon - Whether to show the right icon
 * @param {number} props.price - Price to be displayed
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {boolean} props.hasRadius - Whether the button has a radius
 * @returns {JSX.Element} - DayButton component
 */
const DayButton = ({ onClick, leftIcon, date, rightIcon, price, disabled, hasRadius }) => {
  const { t } = useTranslation('general');
  const { HIDE_NAVIGATION_MIN_PRICES } = useWhitelabelFeatures();
  let textcolor = 'grayMedium';

  if (disabled) {
    textcolor = 'grayLight';
  }

  return (
    <button
      onClick={onClick}
      className={`rounded-button ${disabled ? 'rounded-button-disabled' : ''} ${
        hasRadius ? 'rounded-button-radius' : ''
      }`}
      disabled={disabled}
    >
      {leftIcon && (
        <Icon type="CircleArrowLeft" hasMargin color={disabled ? 'grayLight' : 'primary'} />
      )}
      <span className="rounded-button-wrapper">
        <Text size="XS" color={textcolor} weight="bold" elementType="span">
          {date}
        </Text>
        {!HIDE_NAVIGATION_MIN_PRICES && Boolean(price) && (
          <span className="rounded-button-price">
            <Text size="XS" color={textcolor} elementType="span">
              {t('from')}
            </Text>
            <LocalizedCurrency
              price={price}
              color={textcolor}
              weight="bold"
              size="S"
              currencyStyle="currency"
            />
          </span>
        )}
      </span>
      {rightIcon && (
        <Icon
          type="CircleArrowRight"
          hasMargin
          marginSide="left"
          color={disabled ? 'grayLight' : 'primary'}
        />
      )}
    </button>
  );
};

DayButton.propTypes = {
  date: PropTypes.string,
  leftIcon: PropTypes.bool,
  rightIcon: PropTypes.bool,
  price: PropTypes.number,
  disabled: PropTypes.bool,
  hasRadius: PropTypes.bool,
  onClick: PropTypes.func,
};

DayButton.defaultProps = {
  date: '',
  leftIcon: false,
  disabled: false,
  rightIcon: false,
  hasRadius: false,
  price: null,
  onClick: () => {},
};

export default DayButton;
